/**
 * GuardianModules — module selector (which features each company runs)
 * and engagement-tier picker (Platform vs Active bundle).
 *
 * Modules are FEATURES, not seats. Turning one on changes coverage
 * (which Report Card domains are scored), not platform price. The price
 * lever is the engagement tier on each row.
 */
import { useState } from "react";
import { ToggleRight, Wand2, CheckCircle2 } from "lucide-react";
import { MODULES, PRICING } from "@/lib/guardianData";

const SELECTABLE_MODULES = MODULES.filter((m) => m.key !== "base");

function tierPrice(tier) {
  return tier === "active" ? PRICING.bundlePerCoMo : PRICING.platformPerCoMo;
}

export default function GuardianModules({ companies, onToggleModule, onSetTier, onSetPortfolioDefault }) {
  const [defaultModules, setDefaultModules] = useState(["base", "vendor", "screening", "monitoring", "training"]);
  const [defaultTier, setDefaultTier] = useState("active");

  const totalMonthly = companies.reduce((sum, c) => sum + tierPrice(c.engagementTier || "platform"), 0);

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div>
        <div className="text-[9px] mb-1" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>/ MODULE SELECTOR · COVERAGE × ENGAGEMENT</div>
        <h2 className="text-2xl font-semibold" style={{ color: "#0F1E2E" }}>Configure what's tracked, per company.</h2>
        <p className="text-sm text-ink-900/65 mt-1 max-w-3xl">
          Modules determine what the expert tracks and which Report Card domains are scored. Modules are <strong>included</strong> in the platform fee — the price lever is the
          engagement tier on each row (Platform = ad-hoc hours · Active bundle = 20 committed hours/mo).
        </p>
      </div>

      {/* Portfolio default applier */}
      <div className="rounded-md border p-4" style={{ borderColor: "rgba(15,30,46,0.08)", background: "rgba(245,180,0,0.06)" }} data-testid="modules-default-panel">
        <div className="flex items-center gap-2 text-[10px] mb-3" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "#C98F00" }}>
          <Wand2 className="h-3 w-3" /> SET PORTFOLIO DEFAULT
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {SELECTABLE_MODULES.map((m) => {
            const on = defaultModules.includes(m.key);
            return (
              <button
                key={m.key}
                onClick={() => setDefaultModules((prev) => prev.includes(m.key) ? prev.filter((x) => x !== m.key) : [...prev, m.key])}
                className="text-xs px-2.5 py-1 rounded-full border transition"
                style={{
                  background: on ? "#F5B400" : "transparent",
                  borderColor: on ? "#F5B400" : "rgba(15,30,46,0.20)",
                  color: on ? "#0F1E2E" : "rgba(15,30,46,0.65)",
                }}
              >{m.name}</button>
            );
          })}
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="inline-flex border rounded-md overflow-hidden" style={{ borderColor: "rgba(15,30,46,0.15)" }}>
            {[
              { id: "platform", label: "Platform · $1,000/mo" },
              { id: "active", label: "Active bundle · $3,225/mo" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setDefaultTier(t.id)}
                className="text-xs px-3 py-1.5 transition"
                style={{
                  background: defaultTier === t.id ? "#0F1E2E" : "white",
                  color: defaultTier === t.id ? "#FDFBF5" : "rgba(15,30,46,0.7)",
                }}
              >{t.label}</button>
            ))}
          </div>
          <button
            onClick={() => {
              if (!window.confirm("Apply this module set + tier to ALL companies? This overwrites their current config.")) return;
              onSetPortfolioDefault(defaultModules, defaultTier);
            }}
            className="text-xs px-3 py-1.5 rounded-md font-medium"
            style={{ background: "#0F1E2E", color: "#FDFBF5", fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.14em" }}
            data-testid="apply-portfolio-default"
          >APPLY TO ALL COMPANIES</button>
        </div>
      </div>

      {/* Matrix */}
      <div className="overflow-x-auto border rounded-md" style={{ borderColor: "rgba(15,30,46,0.08)" }} data-testid="modules-matrix">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr style={{ background: "rgba(245,180,0,0.08)" }}>
              <th className="text-left px-3 py-2 text-[10px] sticky left-0 z-10 bg-cream-100" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.16em", color: "rgba(15,30,46,0.55)", background: "#FBF4DC" }}>COMPANY</th>
              {MODULES.map((m) => (
                <th key={m.key} className="text-center px-2 py-2 text-[10px]" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.12em", color: "rgba(15,30,46,0.55)" }} title={m.description}>
                  {m.name.replace(/ \/ .*/, "").replace("Base Guardian tenant", "Base").replace("Client Onboarding (CDD/EDD)", "Onboarding").replace("Marketing & Advertising Review", "Marketing").replace("Monitoring & Testing", "Monitoring")}
                </th>
              ))}
              <th className="text-left px-3 py-2 text-[10px]" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.16em", color: "rgba(15,30,46,0.55)" }}>TIER</th>
              <th className="text-right px-3 py-2 text-[10px]" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.16em", color: "rgba(15,30,46,0.55)" }}>$/MO</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((c, i) => {
              const tier = c.engagementTier || "platform";
              return (
                <tr key={c.id} className={i % 2 ? "" : "bg-cream-50/50"} style={{ borderTop: "1px solid rgba(15,30,46,0.06)" }}>
                  <td className="px-3 py-2 font-medium" style={{ color: "#0F1E2E" }}>{c.name}<div className="text-[10px] text-ink-900/45" style={{ fontFamily: "ui-monospace, SF Mono, monospace" }}>{c.vertical}</div></td>
                  {MODULES.map((m) => {
                    const on = c.activeModules.includes(m.key);
                    const locked = m.key === "base";
                    return (
                      <td key={m.key} className="px-2 py-2 text-center">
                        <button
                          disabled={locked}
                          onClick={() => onToggleModule(c.id, m.key)}
                          className="h-6 w-6 rounded inline-flex items-center justify-center text-[10px] font-semibold transition"
                          style={{
                            background: on ? "#F5B400" : "rgba(15,30,46,0.06)",
                            color: on ? "#0F1E2E" : "rgba(15,30,46,0.4)",
                            cursor: locked ? "default" : "pointer",
                            opacity: locked ? 0.85 : 1,
                          }}
                          title={`${m.name} — ${m.description}`}
                          data-testid={`mod-${c.id}-${m.key}`}
                        >{on ? "✓" : ""}</button>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2">
                    <select
                      value={tier}
                      onChange={(e) => onSetTier(c.id, e.target.value)}
                      data-testid={`tier-${c.id}`}
                      className="text-xs px-2 py-1 border rounded"
                      style={{ borderColor: "rgba(15,30,46,0.15)" }}
                    >
                      <option value="platform">Platform</option>
                      <option value="active">Active bundle</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right font-semibold" style={{ color: tier === "active" ? "#C98F00" : "#0F1E2E", fontFamily: "ui-monospace, SF Mono, monospace" }} data-testid={`tier-price-${c.id}`}>
                    ${tierPrice(tier).toLocaleString()}
                  </td>
                </tr>
              );
            })}
            <tr style={{ background: "#0F1E2E", color: "#FDFBF5" }}>
              <td className="px-3 py-2.5 font-semibold" colSpan={MODULES.length + 1}>Portfolio platform + bundle total</td>
              <td className="px-3 py-2.5"></td>
              <td className="px-3 py-2.5 text-right font-bold" style={{ color: "#F5B400", fontFamily: "ui-monospace, SF Mono, monospace" }} data-testid="modules-total">
                ${totalMonthly.toLocaleString()}/mo
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Hover/title key */}
      <div className="text-[11px] text-ink-900/55 leading-relaxed max-w-3xl">
        <strong style={{ color: "#0F1E2E" }}>Note on screening:</strong> ComplyCube carries a per-check vendor cost that can be passed through to companies — it's a usage line, not a seat fee. The platform price stays $1,000/co regardless of modules. The engagement tier is the real lever.
      </div>
    </div>
  );
}
